import React from 'react';
import cuid from 'cuid';
import Immutable from 'immutable';
import { get, sanitizeInnerHtml, interpolateString, pageContext, executeTransform, executeSelector } from 'ctm-util-functions';
import { HelpIndicator } from './form';
import { Icon } from './icon/icon';

/**
 * Apply the results of one or more selectors to a string
 * @param {String} text - The text to resolve - if there are no selectors specified for the text, it will be returned as-is
 * @param {Object} props - The set of component props
 * @param {Object} state - The app state
 * @returns {String} - The text with any selector values added
 */
const resolveTextTemplate = (text, props, state = null) => {
    if (!state) {
        // I'm not super happy with this, but it saves us from hooking up a LOT of component instances (mainly Text) to state
        try {
            // eslint-disable-next-line no-param-reassign
            state = pageContext.getProps().store.getState();
        } catch (e) {
            // eslint-disable-next-line no-param-reassign
            state = Immutable.Map();
            // eslint-disable-next-line no-console
            console.warn("resolveTextTemplate doesn't have access to the store, if you're seeing this outside of a unit test, something has gone very wrong.");
        }
    }
    const selectedValues = getSelectedValues(state, props);
    return interpolateString(text)(selectedValues);
};

/**
 * Return the set of default values (ie field.valus[i].value) for a range of form element types - badegroup, radiogroup, checkbox
 * @param {Object} schema - The form element schema
 * @returns {Array} - The set of default values used by the form element
 */
const getDefaultValues = (schema) => {
    if (!schema) {
        return [];
    }

    const { type = 'fieldSet', values = [], value = null } = schema.field || {};
    if ((type === 'badgegroup' || type === 'radiogroup') && values.length) {
        return values.reduce((acc, val) => [...acc, val.value], []);
    }
    if (type === 'checkbox' || type === 'badgegroup') {
        return [value];
    }
    return [];
};

/**
 * Convert the results of a selector to lower, upper or title case, or no change
 * @param {String|*} value - The value to change the case of
 * @param {String} casing - The type of casing to apply
 * @returns {String|*} - The original string with the casing applied or the original value if no casing was specified
 * or if value is not a string
 */
const convertStringCase = (value, casing) => {
    if (!value || typeof value !== 'string') {
        return value;
    }

    switch (casing) {
        case 'lower': {
            return value.toLowerCase();
        }
        case 'upper': {
            return value.toUpperCase();
        }
        case 'title': {
            // @todo currently this will only title case the first word
            return `${value[0].toUpperCase()}${value.slice(1)}`;
        }
        default: {
            return value;
        }
    }
};

/**
 * Checks if assumed question type is present in array
 * @param {array} assumedQuestionsArray - The list of questions which are assumed
 * @return {questionType} - The question type to check if its part of the array
 */

const hasAssumedQuestionType = (assumedQuestionsArray, questionType) => {
    return assumedQuestionsArray?.some((question) => question?.questionId === questionType);
};

/**
 * Gets the licence type label for a provided key
 * @param {String} value - The value of the licence type key
 * @return {String} - The full licence type string for the supplied licence type key
 */

const getLicenceTypeLabel = (value) => {
    switch (value) {
        case 'FULL':
            return 'Full / Open Licence';
        case 'PROVISIONAL2':
            return 'Provisional 2 (Green)';
        case 'PROVISIONAL1':
            return 'Provisional 1 (Red)';
        case 'LEARNERS':
            return 'Learners';
        case 'FOREIGN':
            return 'Foreign Licence';
        default:
            return value;
    }
};

/**
 * Gets the description string for the car
 * @param {Array} descriptions - The descriptions array object
 * @return {String} - The full description string constructed from the descriptions object
 */

export const getCarDescription = (descriptions) => {
    if (descriptions) {
        const keys = ['SERIES', 'BADGE', 'ENGINE', 'TRANSMISSION', 'POWER', 'DRIVE', 'KERBMASS', 'RELEASE'];
        return keys.map((key) => {
            const description = descriptions[key]?.description;
            if (description && key === 'RELEASE') {
                const label = descriptions[key]?.label ?? '';
                return `${description} ${label}`;
            }
            return description;
        }).filter((description) => !!description).join(' | ');
    }
    return '';
};
/**
 * Get any specified values for dynamically changing labels / text etc via selectors from state
 * @param {Object} state - The application state
 * @param {Object} props - The FieldRow props
 * @returns {Object} - The selected values, mapped to the selector name. For form elements that contain multiple elements with values,
 *                     the values are mapped to the selectorName_defaultValue
 */
export const getSelectedValues = (state, props) => {
    const executors = {
        transform: executeTransform,
        selector: executeSelector
    };

    const selectedValues = {};
    // @todo: only vertical specific selectors for now - need to group selectors from ctm-selectors that only return primitives, most return objects

    const defaultValues = getDefaultValues(props.schema);
    let selectorNames = get(props, 'schema.selectors.names', {});
    if (!Object.keys(selectorNames).length) {
        selectorNames = get(props, 'selectors.names', {});
    }

    for (const name of Object.keys(selectorNames)) {
        const selectorExpr = selectorNames[name];
        const executor = executors[selectorExpr.split(':')[0]];

        const selectedValue = executor(selectorExpr, state) || get(props, `schema.selectors.props.${name}.default`);
        const casing = get(props, `schema.selectors.props.${name}.case`);

        selectedValues[name] = convertStringCase(selectedValue, casing);
        if (defaultValues.length && get(props, 'schema.selectors.useDefaultValues', false)) {
            // The selector has been specified for the content of a form element - eg radiogroup. But the conditions
            // for selecting a value to substitute may not have been set yet. eg coverType badges for HnC, on first load
            // we don't know if it's for landlord or owner and the coverType comes from the individual radio values, not
            // the value set on state when a radio is selected. So the selector provides a default eg owner or landlord
            // and we map the values to the selectorName_defaultValue so that each radio can use a different selected value from the same selector.
            for (const value of defaultValues) {
                selectedValues[`${name}_${value}`] = executor(selectorExpr, state, value);
            }
        }
    }
    return selectedValues;
};

/**
 * Content Props Schemas expected: see the unit tests for example schema use cases!
 * Util method to include a label or/and an icon to the button.
 * @param {Object} props - properties of the component.
 * @returns {Array} Content elements to be rendered
 */
const getContent = (props = {}) => {
    const label = getLabel(props);
    let content = [];
    if (label.length) {
        content = label;
    }
    const { icon, iconPosition = 'after', iconColor = 'inherit', children, className, tagName, ...rest } = props;

    if (children) {
        content.push(children);
    }

    if (icon) {
        const margin = iconPosition === 'after' ? 'left' : 'right';
        const componentIcon = <Icon {...rest} key={cuid()} variant={icon} color={iconColor} margin={margin} />;

        if (iconPosition === 'after') {
            content.push(componentIcon);
        } else {
            content.unshift(componentIcon);
        }
    }
    return content;
};

/**
 * Retrieves a value from state if a stateKey is provided (formats as either html or plain text),
 * or if a static value is provided returns that as well,
 * and also does innerHTML if there is an innerHTML
 * @param {Object} props The props
 * @returns {Array} The text strings
 * @Todo Refactor this - gotta be a cleaner solution
 */
const getLabel = (props) => {
    let label = null;
    let helpText = null;
    const composedLabel = [];
    const suffix = props.suffix || null;
    const prefix = props.prefix || null;

    if (props.stateProps && props.stateKey) {
        const labelText = get(props.stateProps, props.stateKey, getSimpleLabel(props));
        if (props.format === 'html') {
            // eslint-disable-next-line react/no-danger
            label = <span key={cuid()} dangerouslySetInnerHTML={{ __html: sanitizeInnerHtml(labelText) }} />;
        } else {
            label = labelText;
        }
    } else if (props.label) {
        label = getSimpleLabel(props);
    } else if (props.innerHTML) {
        const labelText = resolveTextTemplate(props.innerHTML, props);
        // eslint-disable-next-line react/no-danger
        label = <span key={cuid()} dangerouslySetInnerHTML={{ __html: sanitizeInnerHtml(labelText) }} />;
    }

    if (label === null || label === undefined || label === '') {
        return composedLabel;
    }

    if (props.helpText) {
        helpText = getSimpleHelpText(props.helpText);
    }

    if (prefix !== null) {
        composedLabel.unshift(prefix);
    }
    if (label !== null) {
        composedLabel.push(label);
    }
    if (suffix !== null) {
        composedLabel.push(suffix);
    }
    if (helpText !== null) {
        composedLabel.push(helpText);
    }

    return composedLabel;
};

/**
 * Return a simple label ; eg when only props.label is defined
 * This is kinda required for now as some components like ExcessDetails which define a label and a stateKey in props
 * but the stateKey isn't required for the label, so getLabel gets confused
 * @todo: review this, identify non-complying components and work out a plan to refactor
 * @param {Object} props - the props of the component to get the label for
 * @param {Object|null} state - the current state or null - if null, state will be retrieved from pageContext
 * @returns {String} - The label with selectors resolved
 */
const getSimpleLabel = (props, state = null) => {
    if (props.rawHtml) {
        // eslint-disable-next-line react/destructuring-assignment
        return <p>{resolveTextTemplate(props.label, props, state)}</p>;
    }
    return resolveTextTemplate(props.label, props, state);
};

const getSimpleHelpText = (helpText) => {
    const isXsBreakpoint = pageContext.getProps().pageState.breakpoint === 'xs';
    const isSmBreakpoint = pageContext.getProps().pageState.breakpoint === 'sm';
    if (isXsBreakpoint) {
        return null;
    }
    if (isSmBreakpoint) {
        return <HelpIndicator contentProps={{ content: helpText, format: 'html' }} schema={{ identifier: 'inline' }} placement={'right'} specialFlags={'results_hnc_tooltip'} />;
    }
    return <HelpIndicator contentProps={{ content: helpText, format: 'html' }} schema={{ identifier: 'inline' }} placement={'left'} specialFlags={'results_hnc_tooltip'} />;
};

const convertObjectKeysToLowercase = (obj) => {
    return Object.keys(obj).reduce((acc, key) => {
        const lowercaseKey = key.toLowerCase();
        acc[lowercaseKey] = obj[key];
        return acc;
    }, {});
};

/**
 * Render a sanitised object with underwriter details
 * @param {List} content - the content to be display in the cell
 * @returns {Object} with the underwriter name, ACN number and AFSL number
 */

const getUnderwriterDetails = (content) => {
    const regex = new RegExp('\\bACN\\b');
    const nameIncludesACN = (contentItem) => regex.test(contentItem);
    const transformedContent = content.toJS();
    const underwriterDetails = { ...transformedContent };
    /*
    Sanitise underwriterDetails for the following cases:
    1. Name has ACN present in it and ACN is present separately
    2. Name has ACN present in it but ACN is not present separately
    */
    if (nameIncludesACN(transformedContent?.name)) {
        const name = transformedContent.name;
        const index = name.search(regex);
        underwriterDetails.name = name.slice(0, index).trim();
        if (!transformedContent?.acn) {
            underwriterDetails.acn = name.substring(index).replace(regex, '').trim();
        }
    }

    return underwriterDetails;
};
const isGalaxyMigrationPhaseOneEnabled = (env) => typeof window !== 'undefined'
    && sessionStorage.getItem('GalaxyMigrationPhaseOneFlag') === 'Y'
    && env !== 'prod' && env !== 'production';

export { getContent, getLabel, getSimpleLabel, convertStringCase, getLicenceTypeLabel, hasAssumedQuestionType,
    resolveTextTemplate, convertObjectKeysToLowercase, getUnderwriterDetails, isGalaxyMigrationPhaseOneEnabled };
